// Documents: everything the customer has sent us, by any route (dashboard,
// WhatsApp, forwarded email, accounts), sorted automatically and checked by
// the team where needed. Upload several at once, or forward emails to the
// customer's own address. Each file can be linked to a claim, item or policy,
// annotated, re-typed (a suggestion staff check) or removed.
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, FileImage, FileSpreadsheet, FileText, Loader2, Mail, MoreHorizontal, Sparkles, Trash2, Upload, UserCheck } from 'lucide-react';
import { ApiError, documentUrl, ok, uploadDocument } from '@cover/core/api/client.ts';
import type { Document, DocumentClass, Link as RecordLink, Source } from '@cover/core/api/types.ts';
import { cn } from '@cover/core/cn.ts';
import { DOC_CLASS_LABEL, SOURCE_SHORT, date, plural } from '@cover/core/format.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { Field, Select, Textarea } from '@cover/core/ui/Form.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, useAssets, useClaims, useConnections, useDocuments, usePolicies, useProfile } from '../api.ts';
import { PageTitle } from '../Layout.tsx';
import { useBusinessId } from '../session.tsx';

const MAX = 16 * 1024 * 1024;

/** How it reached us, in the customer's terms. */
const VIA: Partial<Record<Source, string>> = { dashboard: 'You uploaded it', document: 'From us', chat: 'Sent in WhatsApp', photo: 'Sent in WhatsApp', voice: 'Sent in WhatsApp', email: 'Forwarded email', staff: 'Added by our team' };
const PROCESSING = new Set(['received', 'scanning', 'classifying']);

const FILTERS: { key: string; label: string; classes: (DocumentClass | null)[] }[] = [
  { key: 'all', label: 'All', classes: [] },
  { key: 'cover', label: 'Cover', classes: ['policy_schedule', 'certificate', 'quote', 'contract'] },
  { key: 'money', label: 'Receipts and invoices', classes: ['receipt', 'invoice', 'bank_statement'] },
  { key: 'claims', label: 'Claims', classes: ['claim_acknowledgement', 'damage_photo', 'police_report'] },
  { key: 'photos', label: 'Photos', classes: ['tool_photo', 'damage_photo'] },
  { key: 'other', label: 'Other', classes: ['email', 'id', 'other', null] },
];

function matches(d: Document, key: string) {
  const f = FILTERS.find((x) => x.key === key)!;
  if (key === 'all') return true;
  if (key === 'claims' && d.links?.some((l) => l.type === 'claim')) return true;
  return f.classes.includes(d.classification ?? null);
}

export function DocumentsPage() {
  const documents = useDocuments();
  const [filter, setFilter] = useState('all');
  return (
    <>
        <PageTitle lead="Send us anything to do with your business or your cover. We sort it automatically, and our team checks anything we’re not sure about.">Documents</PageTitle>
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <UploadCard />
            <ForwardCard />
          </div>
          <Card>
            <CardHeader title="Everything you’ve sent us" />
            <CardBody className="pt-3">
              <Load q={documents}>
                {(all) => {
                  const docs = all;
                  if (!docs.length) return <Empty title="Nothing here yet">Upload a file, forward an email or send a photo in WhatsApp.</Empty>;
                  const shown = docs.filter((d) => matches(d, filter));
                  return (
                    <>
                      <div className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1" role="group" aria-label="Show">
                        {FILTERS.map((f) => {
                          const n = docs.filter((d) => matches(d, f.key)).length;
                          if (!n && f.key !== 'all') return null;
                          return (
                            <button
                              key={f.key}
                              type="button"
                              aria-pressed={filter === f.key}
                              onClick={() => setFilter(f.key)}
                              className={cn('min-h-11 shrink-0 rounded-full border px-4 text-sm font-medium', filter === f.key ? 'border-brand bg-brand text-on-brand' : 'border-line bg-white text-ink hover:bg-canvas')}
                            >
                              {f.label} <span className={filter === f.key ? 'opacity-80' : 'text-mid'}>{n}</span>
                            </button>
                          );
                        })}
                      </div>
                      <DocList docs={shown} />
                    </>
                  );
                }}
              </Load>
            </CardBody>
          </Card>
        </div>
    </>
  );
}

/** Pick or drop several files; each uploads in turn and appears below as it's sorted. */
function UploadCard() {
  const b = useBusinessId();
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<{ done: number; total: number } | null>(null);
  const [over, setOver] = useState(false);

  async function upload(files: File[]) {
    if (!files.length) return;
    const tooBig = files.filter((f) => f.size > MAX);
    const ok = files.filter((f) => f.size <= MAX);
    if (tooBig.length) toast.fail(new Error(`${tooBig.map((f) => f.name).join(', ')} ${tooBig.length === 1 ? 'is' : 'are'} over 16 MB.`));
    setBusy({ done: 0, total: ok.length });
    let sent = 0;
    for (const f of ok) {
      try {
        await uploadDocument(b, f, { source: 'dashboard' });
        sent++;
      } catch (e) {
        toast.fail(e);
      }
      setBusy({ done: sent, total: ok.length });
    }
    setBusy(null);
    if (input.current) input.current.value = '';
    if (camera.current) camera.current.value = '';
    await qc.invalidateQueries({ queryKey: keys.documents(b) });
    if (sent) toast.ok(`${plural(sent, 'file')} sent. We’ll sort ${sent === 1 ? 'it' : 'them'} in a moment.`);
  }

  return (
    <Card
      className={cn('border-dashed', over && 'border-brand bg-brand/5')}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void upload([...e.dataTransfer.files]);
      }}
    >
      <CardBody className="flex h-full flex-col gap-3">
        <div className="flex items-start gap-3">
          <Upload className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden />
          <div>
            <h2 className="font-semibold">Upload</h2>
            <p className="text-sm text-mid">Photos, PDFs, receipts, schedules from other insurers. Drop them here or choose files.</p>
          </div>
        </div>
        <input ref={input} type="file" multiple accept="image/*,application/pdf,.heic,.csv,.eml" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => upload([...(e.target.files ?? [])])} />
        <input ref={camera} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => upload([...(e.target.files ?? [])])} />
        <div className="mt-auto flex flex-wrap gap-2">
          <Button type="button" busy={!!busy} onClick={() => input.current?.click()}>
            {!busy && <Upload className="h-4 w-4" aria-hidden />}
            {busy ? `Sending ${busy.done + 1} of ${busy.total}…` : 'Choose files'}
          </Button>
          <Button type="button" variant="secondary" disabled={!!busy} onClick={() => camera.current?.click()} className="sm:hidden">
            Take a photo
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ForwardCard() {
  const profile = useProfile();
  const connections = useConnections();
  const toast = useToast();
  const address = connections.data?.find((c) => c.provider === 'email_forward')?.address ?? profile.data?.forwardingAddress ?? null;
  async function copy() {
    try {
      await navigator.clipboard.writeText(address!);
      toast.ok('Address copied.');
    } catch {
      toast.fail(new Error('Couldn’t copy. Press and hold the address to copy it.'));
    }
  }
  return (
    <Card>
      <CardBody className="flex h-full flex-col gap-3">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden />
          <div>
            <h2 className="font-semibold">Forward emails</h2>
            <p className="text-sm text-mid">Forward receipts, invoices and anything from another insurer to your own address. Attachments land here.</p>
          </div>
        </div>
        {address ? (
          <div className="mt-auto flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 select-all break-all rounded-md bg-canvas px-3 py-2.5 text-sm">{address}</code>
            <Button variant="secondary" onClick={copy} aria-label={`Copy ${address}`}>
              <Copy className="h-4 w-4" aria-hidden /> Copy
            </Button>
          </div>
        ) : (
          <Button asChild variant="secondary" className="mt-auto">
            <Link to="/connections?provider=email_forward" className="text-ink no-underline hover:no-underline">
              Get your address
            </Link>
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

function DocIcon({ doc }: { doc: Document }) {
  const cls = 'h-5 w-5 shrink-0 text-mid';
  if (doc.contentType.startsWith('image/')) return <FileImage className={cls} aria-hidden />;
  if (doc.contentType.includes('csv')) return <FileSpreadsheet className={cls} aria-hidden />;
  return <FileText className={cls} aria-hidden />;
}

/** Where a document stands: being read, sorted automatically, with the team, or checked. */
function DocStatus({ d }: { d: Document }) {
  if (PROCESSING.has(d.status))
    return (
      <span className="inline-flex items-center gap-1 text-mid">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Reading it…
      </span>
    );
  if (d.status === 'quarantined') return <Badge tone="bad">Blocked: couldn’t open it safely</Badge>;
  if (d.status === 'failed') return <span className="text-mid">Couldn’t read this one; it’s still saved</span>;
  if (d.status === 'needs_review' && d.proposedClassification)
    return (
      <span className="inline-flex items-center gap-1 text-info-fg">
        <UserCheck className="h-3.5 w-3.5" aria-hidden /> You suggested “{DOC_CLASS_LABEL[d.proposedClassification]}”; our team is checking
      </span>
    );
  if (d.status === 'needs_review')
    return (
      <span className="inline-flex items-center gap-1 text-info-fg">
        <UserCheck className="h-3.5 w-3.5" aria-hidden /> Our team is checking it
      </span>
    );
  if (d.reviewedAt)
    return (
      <span className="inline-flex items-center gap-1 text-ok-fg">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Checked by our team
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-mid">
      <Sparkles className="h-3.5 w-3.5" aria-hidden /> Sorted automatically
    </span>
  );
}

/** Tags worth showing a customer: not the type again, not internal markers like "linked:asset". */
function shownTags(d: Document): string[] {
  const hide = new Set(['needs review', 'forwarded', d.classification ?? '', d.classification ? DOC_CLASS_LABEL[d.classification].toLowerCase() : '']);
  return (d.tags ?? []).filter((t) => !t.includes(':') && !hide.has(t.toLowerCase()));
}

export function DocList({ docs }: { docs: Document[] }) {
  const [managing, setManaging] = useState<Document | null>(null);
  const claims = useClaims();
  const assets = useAssets();
  const names = useMemo(() => {
    const m = new Map<string, { label: string; to: string }>();
    for (const c of claims.data ?? []) m.set(`claim:${c.id}`, { label: `Claim ${c.reference}`, to: `/claims/${c.id}` });
    for (const a of assets.data ?? []) m.set(`asset:${a.id}`, { label: a.name, to: '/equipment' });
    return m;
  }, [claims.data, assets.data]);

  if (!docs.length) return <p className="py-6 text-center text-mid">Nothing of this kind yet.</p>;
  return (
    <>
    {managing && <ManageDocument doc={managing} onClose={() => setManaging(null)} />}
    <ul className="divide-y divide-line">
      {docs.map((d) => {
        const links = (d.links ?? []).map((l) => (l.type === 'policy' ? { label: `Policy ${l.id}`, to: '/policies' } : names.get(`${l.type}:${l.id}`))).filter((x): x is { label: string; to: string } => !!x);
        return (
          <li key={d.id} className="flex gap-3 py-3">
            <DocIcon doc={d} />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                {d.status === 'quarantined' ? (
                  <span className="truncate font-medium">{d.filename}</span>
                ) : (
                  <a href={documentUrl(d.id)} target="_blank" rel="noopener" className="max-w-full truncate font-medium">
                    {d.summary ?? d.filename}
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {d.classification && <Badge tone="neutral">{DOC_CLASS_LABEL[d.classification]}</Badge>}
                {shownTags(d).map((t) => (
                  <Badge key={t} tone="neutral">
                    {t}
                  </Badge>
                ))}
                {links.map((l) => (
                  <Link key={l.to + l.label} to={l.to} className="text-sm">
                    {l.label}
                  </Link>
                ))}
              </div>
              <p className="flex flex-wrap gap-x-2 text-sm text-mid">
                <DocStatus d={d} />
                <span aria-hidden>·</span>
                <span>
                  {VIA[d.source] ?? SOURCE_SHORT[d.source]}, {date(d.createdAt)}
                </span>
              </p>
              {d.note && <p className="text-sm">“{d.note}”</p>}
              {d.summary && <p className="truncate text-xs text-muted">{d.filename}</p>}
            </div>
            {d.status !== 'quarantined' && !PROCESSING.has(d.status) && (
              <Button variant="ghost" size="sm" className="min-h-11 shrink-0 self-start" onClick={() => setManaging(d)} aria-label={`Manage ${d.summary ?? d.filename}`}>
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </li>
        );
      })}
    </ul>
    </>
  );
}

/** Our own quote, policy and claim PDFs stay; so does claim evidence. */
function canRemove(d: Document) {
  return d.source !== 'document' && !['quote', 'certificate', 'claim_acknowledgement'].includes(d.classification ?? '') && !d.links?.some((l) => l.type === 'claim');
}

const CLASSES = (Object.entries(DOC_CLASS_LABEL) as [DocumentClass, string][]).filter(([k]) => !['quote', 'certificate', 'claim_acknowledgement'].includes(k));

/** Link a document to a claim, item or policy, add a note, correct its type, or remove it. */
function ManageDocument({ doc, onClose }: { doc: Document; onClose: () => void }) {
  const b = useBusinessId();
  const qc = useQueryClient();
  const toast = useToast();
  const claims = useClaims();
  const assets = useAssets();
  const policies = usePolicies();
  const current = doc.links?.find((l) => l.type !== 'fact');
  const [link, setLink] = useState(current ? `${current.type}:${current.id}` : '');
  const [note, setNote] = useState(doc.note ?? '');
  const [kind, setKind] = useState<DocumentClass | ''>(doc.proposedClassification ?? doc.classification ?? '');
  const [removing, setRemoving] = useState(false);
  const issued = !canRemove(doc) && !doc.links?.some((l) => l.type === 'claim');
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: keys.documents(b) }), qc.invalidateQueries({ queryKey: keys.assets(b) })]);

  const save = useMutation({
    mutationFn: () => {
      const [type, id] = link.split(/:(.*)/s) as [RecordLink['type'], string];
      const retyped = kind && kind !== doc.classification && kind !== doc.proposedClassification;
      return ok(
        api.PATCH('/documents/{documentId}', {
          params: { path: { documentId: doc.id } },
          body: { links: link ? [{ type, id }] : [], note: note.trim() || null, ...(retyped ? { classification: kind } : {}) },
        }),
      );
    },
    onSuccess: async (d) => {
      await refresh();
      toast.ok(d.proposedClassification && d.proposedClassification !== doc.proposedClassification ? 'Saved. Our team will check the new type.' : 'Saved.');
      onClose();
    },
    onError: (e) => toast.fail(e),
  });
  const remove = useMutation({
    mutationFn: () => ok(api.DELETE('/documents/{documentId}', { params: { path: { documentId: doc.id } } })),
    onSuccess: async () => {
      await refresh();
      toast.ok('Removed.');
      onClose();
    },
    onError: (e) => toast.fail(e instanceof ApiError && e.status === 409 ? new Error(e.code === 'in_claim' ? 'This is part of a claim, so it stays.' : 'We issued this one, so it stays.') : e),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={doc.summary ?? doc.filename} description={doc.summary ? doc.filename : undefined}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="What’s it for?" hint="Link it to a claim, an item or a policy so it’s in the right place.">
          <Select value={link} onChange={(e) => setLink(e.target.value)}>
            <option value="">Nothing in particular</option>
            {!!claims.data?.length && (
              <optgroup label="Claims">
                {claims.data.map((c) => (
                  <option key={c.id} value={`claim:${c.id}`}>
                    Claim {c.reference}
                  </option>
                ))}
              </optgroup>
            )}
            {!!assets.data?.length && (
              <optgroup label="Equipment">
                {assets.data
                  .filter((a) => a.status !== 'removed')
                  .map((a) => (
                    <option key={a.id} value={`asset:${a.id}`}>
                      {a.name}
                    </option>
                  ))}
              </optgroup>
            )}
            {!!policies.data?.length && (
              <optgroup label="Policies">
                {policies.data.map((p) => (
                  <option key={p.policyNumber} value={`policy:${p.policyNumber}`}>
                    {p.productName ?? p.product} ({p.policyNumber})
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Field>
        {!issued && (
          <Field label="What kind of document is it?" hint={doc.classification ? 'We sorted it automatically. If that’s wrong, change it and our team will check.' : undefined}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as DocumentClass)}>
              {!kind && <option value="">Not sure</option>}
              {CLASSES.map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Note" hint="Anything we should know about it">
          <Textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" busy={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
        {canRemove(doc) && (
          <div className="border-t border-line pt-3">
            {removing ? (
              <div className="space-y-2 rounded-md bg-bad-bg p-3" role="alert">
                <p className="text-sm text-bad-fg">Remove this document? Only do this if you sent it by mistake.</p>
                <div className="flex gap-2">
                  <Button type="button" variant="danger" size="sm" className="min-h-11" busy={remove.isPending} onClick={() => remove.mutate()}>
                    Yes, remove it
                  </Button>
                  <Button type="button" variant="secondary" size="sm" className="min-h-11" onClick={() => setRemoving(false)}>
                    Keep it
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="min-h-11 text-bad-fg hover:bg-bad-bg" onClick={() => setRemoving(true)}>
                <Trash2 className="h-4 w-4" aria-hidden /> Remove this document
              </Button>
            )}
          </div>
        )}
      </form>
    </Dialog>
  );
}
